import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PencilLine, Undo2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";

const FIELD_LABEL = { description: "manual_edit.field_description", specification: "manual_edit.field_specification" };

export function ManualEditMarker({ row, onRevert }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [busy, setBusy] = useState(null);
  const fields = Object.keys(row.manual_fields || {});
  if (!fields.length) return null;
  const canRevert = user && ["admin", "ci_admin"].includes(user.role);

  const revert = async (field) => {
    if (!window.confirm(t("manual_edit.confirm", { field: t(FIELD_LABEL[field]) }))) return;
    setBusy(field);
    try { await onRevert?.(row, field); } finally { setBusy(null); }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          title={t("manual_edit.marker_title")}
          data-testid={`manual-edit-marker-${row.product_id}`}
          className="flex-shrink-0 mt-0.5 text-amber-500 hover:text-amber-700 transition-colors"
        >
          <PencilLine size={13} strokeWidth={2} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-3 text-xs space-y-2" onClick={(e) => e.stopPropagation()} data-testid={`manual-edit-popover-${row.product_id}`}>
        <div className="font-semibold text-amber-700 flex items-center gap-1"><PencilLine size={12} /> {t("manual_edit.title")}</div>
        {fields.map((f) => {
          const fm = row.fm_values?.[f];
          return (
            <div key={f} className="rounded-md border border-gray-100 bg-gray-50 p-2 space-y-1" data-testid={`manual-edit-field-${f}-${row.product_id}`}>
              <div className="font-medium text-gray-700">{t(FIELD_LABEL[f])}</div>
              <div className="text-gray-500">
                <span className="text-gray-400">{t("manual_edit.fm_value")}:</span>{" "}
                {fm != null && fm !== "" ? <span className="text-gray-700 break-words">{String(fm)}</span> : <span className="italic">{t("manual_edit.no_snapshot")}</span>}
              </div>
              {canRevert && (
                <Button
                  size="sm" variant="outline" disabled={busy === f}
                  onClick={() => revert(f)}
                  data-testid={`manual-edit-revert-${f}-${row.product_id}`}
                  className="h-7 text-xs gap-1 w-full"
                >
                  <Undo2 size={12} /> {busy === f ? t("manual_edit.reverting") : t("manual_edit.revert")}
                </Button>
              )}
            </div>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
